import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  LocationOn as SiteIcon,
  Apartment as BuildingIcon,
  FlagCircle as AssemblyIcon,
} from '@mui/icons-material';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import { appColors } from '../Config/Theme';

const PREVIEW_SITES = [
  { name: '149 Commonwealth Drive', city: 'Menlo Park, CA', buildings: ['Building A', 'Building B', 'Research Wing'] },
  { name: '200 Main Street', city: 'San Francisco, CA', buildings: ['Tower 1', 'Tower 2'] },
];

const PREVIEW_ASSEMBLY = [
  { name: 'Front Parking Lot', capacity: '200 people', site: '149 Commonwealth Drive' },
  { name: 'Rear Field', capacity: '150 people', site: '149 Commonwealth Drive' },
  { name: 'City Park North', capacity: '300 people', site: '200 Main Street' },
];

const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <Box
    sx={{
      bgcolor: appColors.surface.card,
      border: `1px solid ${appColors.border.default}`,
      borderRadius: '12px',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        borderBottom: `1px solid ${appColors.border.default}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: appColors.surface.page,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '8px',
          bgcolor: appColors.primary.light,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: appColors.primary.main,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 2.5, py: 2 }}>
      {children}
    </Box>
  </Box>
);

const SettingsLocationsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      {/* ── Breadcrumbs ── */}
      <AppBreadcrumbs items={[
        { label: 'Settings', path: '/settings' },
        { label: 'Locations & Assembly Areas' },
      ]} />

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<BackIcon />}
            onClick={() => navigate('/settings')}
            sx={{ color: appColors.text.body }}
          >
            Back to Settings
          </Button>
        </Box>
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 0.5 }}>
        Locations & Assembly Areas
      </Typography>
      <Typography variant="body2" sx={{ color: appColors.text.body, mb: 3, maxWidth: 700 }}>
        Manage site/building assembly area configuration used during incident creation and accountability workflows.
      </Typography>

      {/* ── Info alert ── */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: '10px' }}>
        This first version defines the UI structure only. Data integration and edit workflows will be added in a later implementation.
      </Alert>

      {/* ── Sections grid ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Sites */}
        <SectionCard icon={<SiteIcon sx={{ fontSize: 20 }} />} title="Sites / Locations">
          {PREVIEW_SITES.map((site) => (
            <Box
              key={site.name}
              sx={{
                py: 1.25,
                borderBottom: `1px solid ${appColors.border.light}`,
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading }}>
                {site.name}
              </Typography>
              <Typography variant="caption" sx={{ color: appColors.text.caption }}>
                {site.city}
              </Typography>
            </Box>
          ))}
          <Typography variant="caption" sx={{ color: appColors.text.placeholder, mt: 1, display: 'block' }}>
            UI preview — {PREVIEW_SITES.length} example sites shown
          </Typography>
        </SectionCard>

        {/* Buildings */}
        <SectionCard icon={<BuildingIcon sx={{ fontSize: 20 }} />} title="Buildings">
          {PREVIEW_SITES.flatMap((site) =>
            site.buildings.map((b) => (
              <Box
                key={`${site.name}-${b}`}
                sx={{
                  py: 1.25,
                  borderBottom: `1px solid ${appColors.border.light}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading }}>
                  {b}
                </Typography>
                <Typography variant="caption" sx={{ color: appColors.text.caption }}>
                  {site.name}
                </Typography>
              </Box>
            )),
          )}
          <Typography variant="caption" sx={{ color: appColors.text.placeholder, mt: 1, display: 'block' }}>
            UI preview — {PREVIEW_SITES.reduce((n, s) => n + s.buildings.length, 0)} example buildings shown
          </Typography>
        </SectionCard>
      </Box>

      {/* Assembly Areas — full width */}
      <SectionCard icon={<AssemblyIcon sx={{ fontSize: 20 }} />} title="Assembly Areas">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          {PREVIEW_ASSEMBLY.map((area) => (
            <Box
              key={area.name}
              sx={{
                bgcolor: appColors.surface.page,
                borderRadius: '8px',
                border: `1px solid ${appColors.border.default}`,
                px: 2,
                py: 1.5,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading }}>
                {area.name}
              </Typography>
              <Typography variant="caption" sx={{ color: appColors.text.body, display: 'block' }}>
                {area.capacity}
              </Typography>
              <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>
                {area.site}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" sx={{ color: appColors.text.placeholder, mt: 1.5, display: 'block' }}>
          UI preview — {PREVIEW_ASSEMBLY.length} example assembly areas shown
        </Typography>
      </SectionCard>
    </Box>
  );
};

export default SettingsLocationsPage;
